import type { UseMutationResult } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Platform, View } from 'react-native'

import { captureException } from '@/src/config/sentry'
import { Typography } from '@/src/elements/text/Typography'
import { NetworkStatus } from '@/src/general/components/NetworkStatus'
import { useToast } from '@/src/general/components/ToastContext'
import { getIsMobile } from '@/src/general/utils/getIsMobile'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TFullMessageApi } from '../../api/types/messageApi.types'
import type { MESSAGE_TYPE } from '../../models/Message.enums'
import { useViewPortSizeStore } from '../screens/MessagesScreen/components/ViewPortSizeView'
import { MessageTextarea } from './MessageTextarea/MessageTextarea'
import type { TMessageTextareaProps } from './MessageTextarea/MessageTextarea.types'

type TConversationFooterProps = {
  userId?: number
  newMessageMutation: UseMutationResult<TFullMessageApi<MESSAGE_TYPE.TEXT>, Error, string>
  onMessageCreated?: (message: TFullMessageApi<MESSAGE_TYPE.TEXT>) => void
  conversationId: number | undefined
}

export const ConversationFooter = ({
  newMessageMutation: { isPending, mutateAsync, data, isIdle },
  onMessageCreated,
  userId,
  conversationId,
}: TConversationFooterProps): ReactNode => {
  const router = useRouter()
  const { routes } = useRoutes()
  const { addToast } = useToast()
  const { t } = useTranslate()
  const { setIsUsingKeyboard } = useViewPortSizeStore()

  const focusProps =
    getIsMobile() && Platform.OS === 'web'
      ? {
          onFocus: () => setIsUsingKeyboard?.(true),
          onBlur: () => setIsUsingKeyboard?.(false),
        }
      : {}

  const onSubmitMessage: TMessageTextareaProps['onSubmitMessage'] = async (content) => {
    try {
      const message = await mutateAsync(content)
      if (onMessageCreated) {
        onMessageCreated(message)
      }
    } catch (error) {
      const caughtError = error instanceof Error ? error : new Error(String(error))
      captureException.then((f) => f(caughtError))
      addToast({ colors: 'danger', message: t('common.error.unknown') })
    }
  }

  const onSubmitPrayer = userId
    ? () => {
        const convId = conversationId ?? data?.conversationId
        if (convId) {
          router.navigate(routes.conversationUserPray(userId, convId))
        } else if (isIdle) {
          router.navigate(routes.conversationUserPray(userId, 'new'))
        } else {
          captureException.then((f) =>
            f(
              new Error(
                `[ConversationFooter] Unable to pray for the user ${JSON.stringify({ isPending, mutateAsync, data, isIdle })} `,
              ),
            ),
          )
        }
      }
    : undefined

  return (
    <View className="flex flex-col p-2 bg-white sm:bg-transparent">
      <NetworkStatus>
        {({ isConnected }) => (
          // `isConnected` can be `null` before NetInfo resolves on native.
          // Treat unknown state as connected to avoid blocking message send button.
          <MessageTextarea
            onSubmitMessage={!isPending && isConnected !== false ? onSubmitMessage : undefined}
            onSubmitPrayer={!isPending && isConnected !== false ? onSubmitPrayer : undefined}
            {...focusProps}
            isLoading={isPending}
          />
        )}
      </NetworkStatus>
      {!getIsMobile() && (
        <Typography size="xs" className="py-1" contrast="low">
          <FormattedMessage id="message.textarea.help.return" />
        </Typography>
      )}
    </View>
  )
}
